import { Add, Clear } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import Editable from "./Editable";
import { SortableItem } from "./SortableItem";

const EditableCard = ({
  id,
  groupId,
  image,
  title,
  description,
  link,
  onUpdate,
  onDelete,
  isAdd,
  isDraggable,
}) => {
  // Temporary copy of all card data -> can be saved or reset to its default state
  const [tempTitle, setTempTitle] = useState("");
  const [tempDescription, setTempDescription] = useState("");
  const [tempLink, setTempLink] = useState("");
  const [tempGroup, setTempGroup] = useState(0);
  const [tempImage, setTempImage] = useState(image);

  const [edited, setEdited] = useState(false);
  const inputRef = useRef();

  function resetDefault() {
    setTempTitle(title);
    setTempDescription(description);
    setTempLink(link);
    setTempGroup(groupId);
    setTempImage(image);
    setEdited(false);
  }

  // Reset when actual card data changes
  useEffect(() => {
    resetDefault();
  }, [title, description, link, image]);

  // Check if card has been edited
  useEffect(() => {
    if (
      title !== tempTitle ||
      description !== tempDescription ||
      link !== tempLink ||
      groupId != tempGroup ||
      image !== tempImage
    ) {
      setEdited(true);
    } else {
      setEdited(false);
    }
  }, [tempTitle, tempDescription, tempLink, tempGroup, tempImage]);

  function handleSave() {
    const updatedCard = {
      id: id,
      groupId: tempGroup,
      image: tempImage,
      title: tempTitle,
      description: tempDescription,
      link: tempLink,
    };
    onUpdate(id, updatedCard);
    if (isAdd) {
      resetDefault();
    }
  }

  function handleAbort() {
    resetDefault();
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "image/svg+xml" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png")
    ) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Bitte laden Sie nur SVG, JPEG, JPG oder PNG Dateien hoch.");
    }
  };

  return (
    <SortableItem
      id={id}
      className="card editable-card"
      isDraggable={isDraggable}
    >
      <div className="editable-card-body">
        <div className="card-image">
          {tempImage ? (
            <img src={tempImage} alt={`${tempTitle} Logo`} />
          ) : isAdd ? (
            <Add fontSize="large" />
          ) : (
            <img
              src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/Pgo8IURPQ1RZUEUgc3ZnIFBVQkxJQyAiLS8vVzNDLy9EVEQgU1ZHIDIwMDEwOTA0Ly9FTiIKICJodHRwOi8vd3d3LnczLm9yZy9UUi8yMDAxL1JFQy1TVkctMjAwMTA5MDQvRFREL3N2ZzEwLmR0ZCI+CjxzdmcgdmVyc2lvbj0iMS4wIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciCiB3aWR0aD0iNDguMDAwMDAwcHQiIGhlaWdodD0iNDguMDAwMDAwcHQiIHZpZXdCb3g9IjAgMCA0OC4wMDAwMDAgNDguMDAwMDAwIgogcHJlc2VydmVBc3BlY3RSYXRpbz0ieE1pZFlNaWQgbWVldCI+Cgo8ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwLjAwMDAwMCw0OC4wMDAwMDApIHNjYWxlKDAuMTAwMDAwLC0wLjEwMDAwMCkiCmZpbGw9IiMwMDAwMDAiIHN0cm9rZT0ibm9uZSI+CjxwYXRoIGQ9Ik0xNTkgNDY2IGMtMTYwIC01NyAtMjExIC0yNTggLTk4IC0zODEgNDMgLTQ3IDkwIC03MyAxNDUgLTgyIDI5IC01CjM0IC0zIDM0IDE0IDAgMTYgLTkgMjIgLTQxIDI2IC00MyA2IC05NyA0MiAtMTMyIDg5IC0yMiAzMCAtMzMgMTA4IC0yMyAxNjMKMTMgNjggMTA0IDE0NSAxNzEgMTQ1IDE4IDAgMjUgNSAyNSAyMCAwIDI0IC0yNyAyNiAtODEgNnoiLz4KPHBhdGggZD0iTTEzNyAzNjAgYy01IC0xOSAwIC0yMCAxMDMgLTIwIDEwMyAwIDEwOCAxIDEwMyAyMCAtNSAxOCAtMTQgMjAKLTEwMyAyMCAtODkgMCAtOTggLTIgLTEwMyAtMjB6Ii8+CjxwYXRoIGQ9Ik0xMjAgMjgwIGMwIC0xOSA2IC0yMCAxMjEgLTIwIDEwOSAwIDEyMCAyIDExNyAxOCAtMyAxNSAtMTggMTcgLTEyMQoyMCAtMTEyIDMgLTExNyAyIC0xMTcgLTE4eiIvPgo8cGF0aCBkPSJNMTMwIDIwMCBjMCAtMTkgNyAtMjAgMTEwIC0yMCAxMDMgMCAxMTAgMSAxMTAgMjAgMCAxOSAtNyAyMCAtMTEwCjIwIC0xMDMgMCAtMTEwIC0xIC0xMTAgLTIweiIvPgo8cGF0aCBkPSJNMTU0IDEyNCBjMyAtOSA2IC0xOCA2IC0yMCAwIC0yIDM2IC00IDc5IC00IDc1IDAgOTEgNiA5MSAzNiAwIDIKLTQxIDQgLTkxIDQgLTc5IDAgLTkwIC0yIC04NSAtMTZ6Ii8+CjwvZz4KPC9zdmc+Cg=="
              alt="BTB Logo"
            ></img>
          )}
        </div>
        <div className="card-info">
          <div className="card-header">
            <h2 className="card-title">
              <Editable
                text={tempTitle}
                placeholder="Titel hinzufügen"
                type="input"
                childRef={inputRef}
              >
                <input
                  ref={inputRef}
                  type="text"
                  name="title"
                  placeholder="Titel hinzufügen"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                />
              </Editable>{" "}
            </h2>

            {!isAdd && (
              <button className="delete-button" onClick={() => onDelete(id)}>
                <Clear fontSize="medium" />
              </button>
            )}
          </div>
          <div className="card-upload-container">
            <label htmlFor={`file-upload-${id}`} className="custom-file-upload">
              Logo hochladen
            </label>
            <div className="card-upload">
              <input
                type="file"
                id={`file-upload-${id}`}
                accept=".svg, .jpeg, .jpg, .png"
                disabled={isDraggable}
                onChange={handleFileChange}
              />
            </div>
            <button
              className="custom-file-upload"
              onClick={() => setTempImage("")}
            >
              Logo entfernen
            </button>
          </div>
          <div className="card-description">
            <Editable
              text={tempDescription}
              placeholder="Beschreibung hinzufügen"
              type="textarea"
              childRef={inputRef}
            >
              <textarea
                ref={inputRef}
                name="description"
                placeholder="Beschreibung hinzufügen"
                rows={2}
                value={tempDescription}
                onChange={(e) => setTempDescription(e.target.value)}
              />
            </Editable>
          </div>
          <div className="card-link">
            <Editable
              text={tempLink}
              placeholder="Link hinzufügen"
              type="input"
              childRef={inputRef}
            >
              <input
                ref={inputRef}
                type="text"
                name="link"
                placeholder="Link hinzufügen"
                value={tempLink}
                onChange={(e) => {
                  if (e.target.value.length > 0) {
                    setTempLink(e.target.value);
                  } else {
                    setTempLink("");
                  }
                }}
              />
            </Editable>
          </div>
          <div className="card-group">
            <span>Gruppe: </span>
            <Editable
              text={tempGroup}
              placeholder="0"
              type="number"
              childRef={inputRef}
            >
              <input
                ref={inputRef}
                type="number"
                name="group"
                placeholder="0"
                value={tempGroup}
                onChange={(e) => {
                  setTempGroup(parseInt(e.target.value));
                }}
              />
            </Editable>
          </div>

          {edited && (
            <div className="card-actions">
              <button className="save-button" onClick={handleSave}>
                Speichern
              </button>
              <button className="abort-button" onClick={handleAbort}>
                Abbrechen
              </button>
            </div>
          )}
        </div>
      </div>
    </SortableItem>
  );
};

export default EditableCard;
